import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SupervisorAssignment from '../components/admin/SupervisorAssignment';
import PageHeader from '../components/PageHeader';
import { useModuleLicense } from '../hooks/useModuleLicense';
import { UserCog, ChevronRight, ChevronDown, Clock, Users, Activity, CalendarDays, Scale, Plug, ClipboardList, HardHat, DollarSign, Award } from 'lucide-react';

function HRManagement() {
  const navigate = useNavigate();
  // One page for both seats: each sees its own half, and either opens the page.
  const { hasAccess: hasHRAccess, loading: hrLoading } = useModuleLicense('timesheet', 'hr');
  const { hasAccess: hasPayrollAccess, loading: payrollLoading } = useModuleLicense('timesheet', 'payroll');
  const [isSupervisorExpanded, setIsSupervisorExpanded] = useState(false);

  if (hrLoading || payrollLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-neutral-500">Loading…</p>
      </div>
    );
  }

  if (!hasHRAccess && !hasPayrollAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <UserCog className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
          <h2 className="text-lg font-semibold text-neutral-700">Access Restricted</h2>
          <p className="text-sm text-neutral-500 mt-1">A Timesheet HR or Payroll license seat is required to access this area.</p>
        </div>
      </div>
    );
  }

  const title = !hasHRAccess ? 'Payroll' : hasPayrollAccess ? 'HR & Payroll' : 'HR Management';
  const subtitle = !hasHRAccess
    ? 'Approve and send the pay run'
    : hasPayrollAccess
      ? 'Manage staff, work schedules and the pay run'
      : 'Manage supervisor assignments, user roles, and work schedules';

  return (
    <div className="min-h-screen bg-gray-50">
      <PageHeader
        title={title}
        subtitle={subtitle}
        icon={hasHRAccess
          ? <UserCog className="h-6 w-6 text-white" />
          : <DollarSign className="h-6 w-6 text-white" />}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Payroll — the Timesheet Payroll seat. */}
        {hasPayrollAccess && (
          <div className="mb-8">
            <button
              onClick={() => navigate('/payroll')}
              className="w-full flex items-center justify-between bg-white border-2 border-emerald-200 rounded-xl p-6 hover:border-emerald-400 hover:shadow-sm transition-all text-left"
            >
              <div className="flex items-center gap-4 min-w-0">
                <div className="bg-emerald-100 p-3 rounded-lg">
                  <DollarSign className="h-6 w-6 text-emerald-600" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-lg font-semibold text-neutral-900">Payroll</h3>
                  <p className="text-sm text-neutral-500 break-words">Approved timesheets by pay period: approve and send each person's pay, or export the pay run</p>
                </div>
              </div>
              <ChevronRight className="h-5 w-5 text-neutral-400 shrink-0" />
            </button>
          </div>
        )}

        {/* Everything from here to Payroll connections is the Timesheet HR seat's. */}
        {hasHRAccess && (<>

        {/* Employee Profile — unified per-employee hub */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/hr/staff')}
            className="w-full flex items-center justify-between bg-white border-2 border-primary-200 rounded-xl p-6 hover:border-primary-400 hover:shadow-sm transition-all text-left"
          >
            <div className="flex items-center gap-4">
              <div className="bg-primary-100 p-3 rounded-lg">
                <UserCog className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">Employee Profile</h3>
                <p className="text-sm text-neutral-500">Pick an employee to manage payroll details, leave entitlements, work schedule, and certifications in one place</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-neutral-400" />
          </button>
        </div>

        {/* Award reviews — classification and pay review reminders */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/hr/award-reviews')}
            className="w-full flex items-center justify-between bg-white border-2 border-amber-200 rounded-xl p-6 hover:border-amber-400 hover:shadow-sm transition-all text-left"
          >
            <div className="flex items-center gap-4 min-w-0">
              <div className="bg-amber-100 p-3 rounded-lg shrink-0">
                <Award className="h-6 w-6 text-amber-600" />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-neutral-900">Award reviews</h3>
                <p className="text-sm text-neutral-500 break-words">Reminders when a birthday, three months' experience or casual conversion may change someone's pay or level, and the 1 July wage review</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-neutral-400 shrink-0" />
          </button>
        </div>

        {/* Workplace Health & Safety */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/hr/whs')}
            className="w-full flex items-center justify-between bg-white border-2 border-orange-200 rounded-xl p-6 hover:border-orange-400 hover:shadow-sm transition-all text-left"
          >
            <div className="flex items-center gap-4">
              <div className="bg-orange-100 p-3 rounded-lg">
                <HardHat className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">Workplace Health &amp; Safety</h3>
                <p className="text-sm text-neutral-500">ISO 45001 aligned WHS system — incident &amp; hazard reporting, risk assessments, corrective actions, inspections, audits, safety documents, training, emergency plans and management review</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-neutral-400" />
          </button>
        </div>

        {/* Live Dashboard */}
        <div>
          <button
            onClick={() => navigate('/hr/live-dashboard')}
            className="w-full flex items-center justify-between bg-white border border-neutral-200 rounded-xl p-6 hover:border-primary-300 hover:shadow-sm transition-all text-left"
          >
            <div className="flex items-center gap-4">
              <div className="bg-emerald-100 p-3 rounded-lg">
                <Activity className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">Live Attendance Dashboard</h3>
                <p className="text-sm text-neutral-500">Real-time view of who is clocked in, clocked out, or on leave today</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-neutral-400" />
          </button>
        </div>

        {/* Pay Cycle Dashboard */}
        <div className="mt-4">
          <button
            onClick={() => navigate('/hr/pay-cycle-dashboard')}
            className="w-full flex items-center justify-between bg-white border border-neutral-200 rounded-xl p-6 hover:border-primary-300 hover:shadow-sm transition-all text-left"
          >
            <div className="flex items-center gap-4">
              <div className="bg-indigo-100 p-3 rounded-lg">
                <CalendarDays className="h-6 w-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">Pay Cycle Dashboard</h3>
                <p className="text-sm text-neutral-500">View employee timesheet data by pay cycle with detailed breakdowns</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-neutral-400" />
          </button>
        </div>

        {/* Supervisor Assignments */}
        <div className="mt-8">
          <button
            onClick={() => setIsSupervisorExpanded(prev => !prev)}
            className="w-full flex items-center justify-between bg-white border border-neutral-200 rounded-xl p-6 hover:border-primary-300 hover:shadow-sm transition-all text-left"
          >
            <div className="flex items-center gap-4">
              <div className="bg-blue-100 p-3 rounded-lg">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">Supervisor Assignments</h3>
                <p className="text-sm text-neutral-500">Assign supervisors to employees for approving hours and leave requests</p>
              </div>
            </div>
            {isSupervisorExpanded
              ? <ChevronDown className="h-5 w-5 text-neutral-400" />
              : <ChevronRight className="h-5 w-5 text-neutral-400" />
            }
          </button>
          {isSupervisorExpanded && (
            <div className="mt-4">
              <SupervisorAssignment />
            </div>
          )}
        </div>

        {/* Work Schedule Settings */}
        <div className="mt-8">
          <button
            onClick={() => navigate('/admin/work-schedule')}
            className="w-full flex items-center justify-between bg-white border border-neutral-200 rounded-xl p-6 hover:border-primary-300 hover:shadow-sm transition-all text-left"
          >
            <div className="flex items-center gap-4">
              <div className="bg-primary-100 p-3 rounded-lg">
                <Clock className="h-6 w-6 text-primary-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">Work Schedule Settings</h3>
                <p className="text-sm text-neutral-500">Set standard work hours, lunch breaks, pay cycle start day, and clock grace window</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-neutral-400" />
          </button>
        </div>

        {/* Staff Certifications */}
        <div className="mt-8">
          <button
            onClick={() => navigate('/certifications/admin')}
            className="w-full flex items-center justify-between bg-white border border-neutral-200 rounded-xl p-6 hover:border-primary-300 hover:shadow-sm transition-all text-left"
          >
            <div className="flex items-center gap-4">
              <div className="bg-teal-100 p-3 rounded-lg">
                <ClipboardList className="h-6 w-6 text-teal-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">Staff Certifications & Licenses</h3>
                <p className="text-sm text-neutral-500">Org-wide register of staff certifications (forklift, first aid, truck licence, ACUP), with type configuration and on-behalf entry</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-neutral-400" />
          </button>
        </div>

        {/* Public Holidays */}
        <div className="mt-8">
          <button
            onClick={() => navigate('/admin/public-holidays')}
            className="w-full flex items-center justify-between bg-white border border-neutral-200 rounded-xl p-6 hover:border-primary-300 hover:shadow-sm transition-all text-left"
          >
            <div className="flex items-center gap-4">
              <div className="bg-amber-100 p-3 rounded-lg">
                <CalendarDays className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">Public Holidays</h3>
                <p className="text-sm text-neutral-500">Configure public holidays for automatic timesheet entries and leave exclusions</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-neutral-400" />
          </button>
        </div>

        </>)}

        {/* Xero Payroll, MYOB and the export mappings are the Accounts module's. */}
        <div className={hasHRAccess ? 'mt-8' : undefined}>
          <button
            onClick={() => navigate('/accounts')}
            className="w-full flex items-center justify-between bg-white border border-neutral-200 rounded-xl p-6 hover:border-primary-300 hover:shadow-sm transition-all text-left"
          >
            <div className="flex items-center gap-4 min-w-0">
              <div className="bg-sky-100 p-3 rounded-lg">
                <Plug className="h-6 w-6 text-sky-600" />
              </div>
              <div className="min-w-0">
                <h3 className="text-lg font-semibold text-neutral-900">Payroll connections</h3>
                <p className="text-sm text-neutral-500 break-words">Xero Payroll, MYOB and the payroll export mappings are set up in Accounts, with an Accounts licence seat</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-neutral-400 shrink-0" />
          </button>
        </div>

        {/* Company Policies */}
        {hasHRAccess && (
        <div className="mt-8">
          <button
            onClick={() => navigate('/hr/company-policies')}
            className="w-full flex items-center justify-between bg-white border border-neutral-200 rounded-xl p-6 hover:border-primary-300 hover:shadow-sm transition-all text-left"
          >
            <div className="flex items-center gap-4">
              <div className="bg-slate-100 p-3 rounded-lg">
                <Scale className="h-6 w-6 text-slate-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-neutral-900">Company Policies</h3>
                <p className="text-sm text-neutral-500">Configure overtime rules, leave accrual, sick leave certificates, and public holiday pay</p>
              </div>
            </div>
            <ChevronRight className="h-5 w-5 text-neutral-400" />
          </button>
        </div>
        )}

      </div>
    </div>
  );
}

export default HRManagement;
